import { memo, useEffect, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useFindingStore, Finding } from "stores/FindingStore";
import { SubmitState } from "stores/lib";

import { GenericObject } from "utility/misc";

import useAnalytics from "effect/use_analytics";

import ChartDisplay from "./ChartDisplay";
import DataDisplay from "./DataDisplay";
import getChartData from "./lib/get_chart_data";
import getPieData from "./lib/get_pie_data";
import getWidgetData from "./lib/get_widget_data";
import PieDisplay from "./PieDisplay";
import Table from "./Table";

// -----------------------------------------------------------------------------

const Dashboard = memo(function Dashboard(): JSX.Element {
  useAnalytics("Dashboard: Home");

  //
  const [store, actions] = useFindingStore();

  //
  const [chartData, setChartData] = useState(getChartData([]));
  const [pieData, setPieData] = useState(getPieData([]));
  const [widgetData, setWidgetData] = useState(
    getWidgetData() as GenericObject
  );

  //
  const loadingFindings =
    store.loadAllState === SubmitState.STARTED ||
    store.loadAllState === SubmitState.STARTED_AGAIN;

  const loadingMetrics = store.loadMetricsState === SubmitState.STARTED;

  //
  useEffect(() => {
    switch (store.loadAllState) {
      case undefined:
        actions.loadAll();
        break;

      case SubmitState.STARTED:
      case SubmitState.STARTED_AGAIN:
      case SubmitState.SUCCESS: {
        const sortedFindings = (store.findingsAll || [])
          .concat()
          .sort(sortByCreateTime);
        setChartData(getChartData(sortedFindings));
        break;
      }
      case SubmitState.ERROR:
        setChartData([]);
    }
  }, [store.findingsAll, store.loadAllState, actions]);

  //
  useEffect(() => {
    switch (store.loadMetricsState) {
      case undefined:
        actions.loadMetrics();
        break;

      case SubmitState.SUCCESS:
        setWidgetData(getWidgetData(store.metrics));
        setPieData(getPieData(store.metrics?.unresolved_by_source));
        break;

      case SubmitState.ERROR:
        setWidgetData({});
        setPieData([]);
        break;

      default:
        break;
    }
  }, [store.loadMetricsState, store.metrics, actions]);

  //
  return (
    <>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Mission Control
      </Typography>

      {store.findingsAllError ? (
        <Alert severity="error">{store.findingsAllError}</Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          mt: 2,
          mb: -2,
          mr: -2,
        }}
      >
        <DataDisplay
          title="Findings (All time)"
          loading={loadingMetrics}
          {...widgetData.findings}
        />
        <DataDisplay
          title="Findings Resolved (All time)"
          loading={loadingMetrics}
          {...widgetData.findings_resolved}
        />
        <DataDisplay
          title="Findings Created (Trailing 30 Days)"
          loading={loadingMetrics}
          {...widgetData.findings_created_last_30_days}
        />
      </Box>

      <Grid
        container
        spacing={2}
        sx={{
          mt: 0.25,
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={9}
          lg={9}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Typography gutterBottom variant="h6" fontWeight="bold">
            New Critical &amp; High Findings
          </Typography>
          <Table />
        </Grid>
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <Typography gutterBottom variant="h6" fontWeight="bold">
            Findings Breakdown
          </Typography>
          <Stack spacing={2}>
            <ChartDisplay
              title="Critical (All time)"
              data={chartData?.critical}
              color="red"
              loading={loadingFindings}
            />
            <ChartDisplay
              title="High (All time)"
              data={chartData?.high}
              color="orange"
              loading={loadingFindings}
            />
            <PieDisplay
              title="Unresolved by source"
              data={pieData ?? []}
              loading={loadingFindings}
            />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
});

export default Dashboard;

// -----------------------------------------------------------------------------

function sortByCreateTime(a: Finding, b: Finding) {
  return a.create_time > b.create_time
    ? 1
    : b.create_time > a.create_time
    ? -1
    : 0;
}
